import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Phone, Snowflake, Check, ArrowRight, ArrowLeft, Star, Home as HomeIcon,
  Wind, Layers, Thermometer, ClipboardList, Search, FileText, Wrench,
} from "lucide-react";
import SEO from "@/components/SEO";
import FinalCTA from "@/components/FinalCTA";
import FAQ from "@/components/FAQ";
import QuickQuote from "@/components/home/QuickQuote";
import RecentInstallations from "@/components/home/RecentInstallations";
import ServiceAreas from "@/components/home/ServiceAreas";
import { Eyebrow, GradientText, PrimaryButton, SecondaryButton, AirflowCurves } from "@/components/Brand";
import { Reveal, MaskedLines, Counter } from "@/lib/motion";
import { SITE, IMAGES, BRANDS } from "@/lib/site";
import { getReviews } from "@/lib/api";

const SERVICES = [
  {
    title: "Ducted Refrigerated",
    description:
      "Discreet whole-home heating and cooling delivered through concealed ductwork, with optional zoning for greater control over different areas of the property.",
    tags: ["Whole Home", "Heating & Cooling", "Zoning Options"],
    cta: "Explore Ducted",
    to: "/ducted-air-conditioning",
    image: "/assets/card_ducted.webp",
    icon: HomeIcon,
    testid: "ducted",
  },
  {
    title: "Split System Air Conditioning",
    description:
      "An efficient and flexible way to heat or cool a bedroom, living area, home office or open-plan space without installing whole-home ductwork.",
    tags: ["Single Room", "Reverse Cycle", "Efficient"],
    cta: "Explore Split Systems",
    to: "/split-system-air-conditioning",
    image: "/assets/card_split.webp",
    icon: Wind,
    testid: "split",
  },
  {
    title: "Multi-Head Split Systems",
    description:
      "Connect several compatible indoor air conditioners to one outdoor condenser and control comfort independently across multiple rooms.",
    tags: ["Multi Room", "Individual Control", "One Outdoor Unit"],
    cta: "Explore Multi-Head",
    to: "/multi-head-split-systems",
    image: "/assets/card_multi.webp",
    icon: Layers,
    testid: "multi-head",
  },
];

const BENEFITS = [
  { num: "01", title: "Tailored System Design", text: "Recommendations based on your property rather than a one-size-fits-all package." },
  { num: "02", title: "Experienced Installation", text: "More than 6,500 installations completed since the business began operating." },
  { num: "03", title: "Leading Air Conditioning Brands", text: "Options across respected air conditioning manufacturers to suit different applications and budgets." },
  { num: "04", title: "After-Sales Support", text: "Our support does not end when the installation is completed." },
];

const STEPS = [
  { num: "01", icon: ClipboardList, title: "Tell Us About Your Home", text: "Tell us what you currently have, the areas you want conditioned and what you would like to improve." },
  { num: "02", icon: Search, title: "We Review Your Options", text: "We consider system type, capacity, property layout and installation requirements." },
  { num: "03", icon: FileText, title: "Receive Your Quote", text: "Receive a clear recommendation and quotation for the proposed system and installation." },
  { num: "04", icon: Wrench, title: "Professional Installation", text: "Our installation team coordinates the work and gets your system installed and commissioned correctly." },
];

const DUCTED_FEATURES = [
  "Whole-home heating and cooling",
  "Optional zoning",
  "Discreet ceiling outlets",
  "Reverse-cycle technology",
  "Suitable for many new and existing homes",
  "Smart-control options on compatible systems",
];

const MARQUEE_ITEMS = [
  "Ducted Refrigerated",
  "Split Systems",
  "Multi-Head Systems",
  "VEU Rebate Assistance",
  "Serving Victoria Since 2000",
  "6,500+ Installations",
  "Free Quotes",
];

function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [perView, setPerView] = useState(3);
  const [page, setPage] = useState(0);

  useEffect(() => {
    getReviews().then(setReviews).catch(() => setReviews([]));
    const update = () =>
      setPerView(window.innerWidth < 640 ? 1 : window.innerWidth < 1024 ? 2 : 3);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const pages = Math.max(1, Math.ceil(reviews.length / perView));
  const visible = reviews.slice(page * perView, page * perView + perView);

  return (
    <section className="bg-offwhite py-24 md:py-32" data-testid="reviews-section">
      <div className="mx-auto max-w-7xl px-5">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Eyebrow>Customer Experiences</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">
              What our customers say.
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              data-testid="reviews-prev"
              aria-label="Previous reviews"
              onClick={() => setPage((p) => (p - 1 + pages) % pages)}
              className="inline-flex h-11 w-11 items-center justify-center rounded-[10px] border border-line bg-white text-navy transition-[border-color,color] duration-200 hover:border-brand-cyan hover:text-brand-blue"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              data-testid="reviews-next"
              aria-label="Next reviews"
              onClick={() => setPage((p) => (p + 1) % pages)}
              className="inline-flex h-11 w-11 items-center justify-center rounded-[10px] border border-line bg-white text-navy transition-[border-color,color] duration-200 hover:border-brand-cyan hover:text-brand-blue"
            >
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </Reveal>

        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="reviews-grid">
          {visible.map((r, i) => (
            <motion.article
              key={r.id}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              className="flex flex-col rounded-[18px] border border-cardline bg-white p-7 shadow-[0_8px_32px_rgba(5,59,117,0.04)]"
              data-testid={`review-card-${i}`}
            >
              <div className="flex gap-1" aria-label={`${r.rating} out of 5 stars`}>
                {Array.from({ length: 5 }).map((_, s) => (
                  <Star
                    key={s}
                    className={`h-4 w-4 ${s < r.rating ? "fill-brand-cyan text-brand-cyan" : "text-line"}`}
                  />
                ))}
              </div>
              <p className="mt-4 flex-1 text-sm leading-relaxed text-ink">“{r.review}”</p>
              <footer className="mt-6 border-t border-cloud pt-4">
                <p className="font-display text-sm font-bold text-navy">{r.customer}</p>
                <p className="text-xs text-mutedblue">{r.location}</p>
              </footer>
            </motion.article>
          ))}
        </div>

        <div className="mt-8 flex justify-center gap-2" data-testid="reviews-dots">
          {Array.from({ length: pages }).map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Go to reviews page ${i + 1}`}
              data-testid={`reviews-dot-${i}`}
              onClick={() => setPage(i)}
              className={`h-2 rounded-full transition-[width,background-color] duration-300 ${
                i === page ? "w-7 bg-brand-blue" : "w-2 bg-line"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const curveY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <>
      <SEO
        title="Air Conditioning Melbourne & Victoria | Airconditioning Victoria"
        description="Airconditioning Victoria has served Victorian homes since 2000 with ducted, split system and multi-head air conditioning. 6,500+ installations. Free quotes."
        path="/"
      />

      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden bg-white" data-testid="home-hero">
        <motion.div style={{ y: curveY }} className="pointer-events-none absolute -right-20 top-10 w-[560px] opacity-60">
          <AirflowCurves />
        </motion.div>
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 pb-20 pt-14 md:pb-28 md:pt-20 lg:grid-cols-[1.05fr_1fr]">
          <div>
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}>
              <Eyebrow>Serving Victoria Since 2000</Eyebrow>
            </motion.div>
            <h1 className="mt-6 font-display text-[40px] font-extrabold leading-[1.05] tracking-tight text-navy sm:text-6xl lg:text-[72px]">
              <MaskedLines
                delay={0.15}
                lines={["Comfort Through Every", <GradientText key="g">Victorian Season.</GradientText>]}
              />
            </h1>
            <motion.p
              className="mt-7 max-w-xl text-base leading-relaxed text-mutedblue md:text-lg"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            >
              Airconditioning Victoria has been helping Victorian households stay comfortable since
              2000. From single-room split systems to multi-head and whole-home ducted air
              conditioning, we provide practical heating and cooling solutions designed around your
              property, lifestyle and budget.
            </motion.p>
            <motion.div
              className="mt-9 flex flex-wrap items-center gap-4"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
            >
              <PrimaryButton to="/contact" testid="hero-quote-btn">Get a Free Quote</PrimaryButton>
              <SecondaryButton to="/ducted-air-conditioning" testid="hero-explore-btn">
                Explore Our Systems
              </SecondaryButton>
            </motion.div>
            <motion.div
              className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.9 }}
            >
              <a
                data-testid="hero-phone-link"
                href={SITE.phoneHref}
                className="inline-flex items-center gap-2 font-display text-sm font-bold text-navy transition-colors duration-200 hover:text-brand-blue"
              >
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-ice">
                  <Phone className="h-4 w-4 text-brand-blue" />
                </span>
                Call {SITE.phone}
              </a>
              <p className="text-xs text-mutedblue">
                Professional heating and cooling solutions for Victorian homes.
              </p>
            </motion.div>
          </div>

          <motion.div
            className="relative"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            <motion.div
              style={{ y: imgY }}
              className="aspect-[1254/1062] overflow-hidden rounded-[24px] border border-cardline shadow-[0_30px_80px_rgba(5,59,117,0.16)]"
            >
              <img
                src="/assets/hero-home.webp"
                alt="Cross-section of a modern Australian home showing multi-room split system air conditioning"
                className="h-full w-full object-cover"
                width="1254"
                height="1062"
                loading="eager"
                fetchPriority="high"
              />
            </motion.div>
            <motion.div
              className="absolute -bottom-6 -left-4 rounded-2xl border border-cardline bg-white p-5 shadow-[0_20px_50px_rgba(5,59,117,0.14)] md:-left-10"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1, ease: [0.22, 1, 0.36, 1] }}
              data-testid="hero-stat-badge"
            >
              <div className="flex items-center gap-3">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-brand">
                  <Snowflake className="h-5 w-5 text-white" />
                </span>
                <div>
                  <p className="font-display text-xl font-extrabold text-navy">6,500+</p>
                  <p className="text-xs font-medium text-mutedblue">Installations Completed</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* STATS */}
      <section className="border-y border-mist bg-cloud py-16 md:py-20" data-testid="stats-section">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-10 px-5 lg:grid-cols-4">
          {[
            { node: <Counter value={6500} suffix="+" />, label: "Installations Completed", testid: "stat-installations" },
            { node: <Counter value={25} suffix="+" />, label: "Years Experience", testid: "stat-experience" },
            { node: <span>Since 2000</span>, label: "Serving Victoria", testid: "stat-since" },
            { node: <Counter value={3} />, label: "Core Air Conditioning Solutions", testid: "stat-solutions" },
          ].map((s, i) => (
            <Reveal key={i} delay={i * 0.1} className="text-center lg:text-left">
              <p data-testid={s.testid} className="font-display text-4xl font-extrabold tracking-tight text-gradient-brand md:text-6xl">
                {s.node}
              </p>
              <p className="mt-2 font-display text-xs font-bold uppercase tracking-[0.18em] text-navy">
                {s.label}
              </p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* MARQUEE */}
      <div className="overflow-hidden border-b border-mist bg-white py-5" aria-hidden="true" data-testid="brand-marquee">
        <div className="flex w-max animate-marquee items-center gap-10">
          {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, i) => (
            <span key={i} className="flex items-center gap-10">
              <span className="whitespace-nowrap font-display text-sm font-bold uppercase tracking-[0.3em] text-navy/70">
                {item}
              </span>
              <Snowflake className="h-4 w-4 shrink-0 text-brand-cyan" />
            </span>
          ))}
        </div>
      </div>

      {/* SERVICES */}
      <section className="bg-white py-24 md:py-32" data-testid="services-section">
        <div className="mx-auto max-w-7xl px-5">
          <Reveal className="max-w-2xl">
            <Eyebrow>Air Conditioning Solutions</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">
              Comfort designed around <GradientText>your home.</GradientText>
            </h2>
            <p className="mt-5 text-base leading-relaxed text-mutedblue md:text-lg">
              Every property is different. We help determine whether a split system, multi-head
              configuration or whole-home ducted reverse-cycle solution is most suitable for your
              home.
            </p>
          </Reveal>

          <div className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.12}>
                <Link
                  to={s.to}
                  data-testid={`service-card-${s.testid}`}
                  className="group flex h-full flex-col overflow-hidden rounded-[20px] border border-cardline bg-white shadow-[0_8px_32px_rgba(5,59,117,0.04)] transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1.5 hover:border-brand-cyan hover:shadow-[0_20px_50px_rgba(5,59,117,0.1)]"
                >
                  <div className="overflow-hidden">
                    <img
                      src={s.image}
                      alt={`${s.title} installation in a Victorian home`}
                      className="aspect-video w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                      loading="lazy"
                    />
                  </div>
                  <div className="flex flex-1 flex-col p-7">
                    <div className="flex items-center gap-3">
                      <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-ice">
                        <s.icon className="h-5 w-5 text-brand-blue" />
                      </span>
                      <h3 className="font-display text-lg font-bold uppercase tracking-wide text-navy">
                        {s.title}
                      </h3>
                    </div>
                    <p className="mt-4 flex-1 text-sm leading-relaxed text-mutedblue">{s.description}</p>
                    <div className="mt-5 flex flex-wrap gap-2">
                      {s.tags.map((t) => (
                        <span key={t} className="rounded-full bg-cloud px-3 py-1 text-[11px] font-semibold text-brand-mid">
                          {t}
                        </span>
                      ))}
                    </div>
                    <span className="mt-6 inline-flex items-center gap-2 font-display text-xs font-bold uppercase tracking-[0.15em] text-brand-blue transition-colors duration-200 group-hover:text-navy">
                      {s.cta}
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* RECENT INSTALLATIONS */}
      <RecentInstallations />

      <Reviews />

      {/* WHY US */}
      <section className="bg-offwhite py-24 md:py-32" data-testid="why-us-section">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 lg:grid-cols-2">
          <Reveal>
            <div className="relative">
              <div className="overflow-hidden rounded-[24px] border border-cardline shadow-[0_24px_60px_rgba(5,59,117,0.1)]">
                <img
                  src="/assets/why-us.webp"
                  alt="Family relaxing in a comfortable living room beneath a wall-mounted air conditioner"
                  className="h-[320px] w-full object-cover md:h-[460px]"
                  loading="lazy"
                />
              </div>
              <div className="absolute -bottom-5 -right-3 rounded-2xl bg-gradient-brand px-6 py-4 text-white shadow-[0_16px_40px_rgba(0,140,196,0.35)] md:-right-6">
                <p className="font-display text-2xl font-extrabold">25+</p>
                <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-ice">Years Experience</p>
              </div>
            </div>
          </Reveal>
          <div>
            <Reveal>
              <Eyebrow>Since 2000</Eyebrow>
              <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">
                Experience you can feel in <GradientText>every room.</GradientText>
              </h2>
              <p className="mt-5 text-base leading-relaxed text-mutedblue">
                Airconditioning Victoria has been working with Victorian homes and properties for
                more than 25 years. With more than 6,500 completed installations, our approach is
                built around practical advice, appropriate system selection and professional
                installation.
              </p>
              <p className="mt-4 text-base leading-relaxed text-mutedblue">
                Instead of recommending the same solution for every property, we consider the size
                and layout of the home, the rooms being conditioned, installation requirements,
                preferred equipment, energy efficiency and budget before recommending a solution.
              </p>
            </Reveal>
            <div className="mt-10 space-y-6">
              {BENEFITS.map((b, i) => (
                <Reveal key={b.num} delay={i * 0.08}>
                  <div className="flex gap-5" data-testid={`benefit-${b.num}`}>
                    <span className="font-display text-2xl font-extrabold text-gradient-brand">{b.num}</span>
                    <div>
                      <h3 className="font-display text-sm font-bold uppercase tracking-[0.15em] text-navy">
                        {b.title}
                      </h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-mutedblue">{b.text}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.3} className="mt-10">
              <PrimaryButton to="/contact" testid="why-us-cta">Talk to Our Team</PrimaryButton>
            </Reveal>
          </div>
        </div>
      </section>

      {/* DUCTED FEATURE */}
      <section className="bg-mist py-24 md:py-32" data-testid="ducted-feature-section">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 lg:grid-cols-2">
          <div className="order-2 lg:order-1">
            <Reveal>
              <Eyebrow>Whole-Home Comfort</Eyebrow>
              <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">
                Discreet comfort <GradientText>throughout your home.</GradientText>
              </h2>
              <p className="mt-5 text-base leading-relaxed text-mutedblue">
                Ducted reverse-cycle air conditioning provides heating and cooling through a network
                of ducts concealed within the ceiling or floor. Only the outlets are visible inside
                your rooms, creating a clean and uncluttered appearance while allowing one system to
                condition multiple areas of the property.
              </p>
            </Reveal>
            <ul className="mt-8 grid gap-3 sm:grid-cols-2">
              {DUCTED_FEATURES.map((f, i) => (
                <Reveal key={f} delay={i * 0.06}>
                  <li className="flex items-start gap-3 text-sm font-medium text-ink">
                    <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gradient-brand">
                      <Check className="h-3 w-3 text-white" />
                    </span>
                    {f}
                  </li>
                </Reveal>
              ))}
            </ul>
            <Reveal delay={0.2} className="mt-10">
              <PrimaryButton to="/ducted-air-conditioning" testid="ducted-feature-cta">
                Explore Ducted Air Conditioning
              </PrimaryButton>
            </Reveal>
          </div>
          <Reveal className="order-1 lg:order-2">
            <div className="overflow-hidden rounded-[24px] border border-cardline shadow-[0_24px_60px_rgba(5,59,117,0.1)]">
              <img
                src="/assets/ducted-feature.webp"
                alt="AirTouch smart zoning controller and app managing a ducted air conditioning system"
                className="h-[320px] w-full object-cover md:h-[460px]"
                loading="lazy"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* VEU */}
      <section className="bg-cloud py-24 md:py-32" data-testid="veu-section">
        <div className="mx-auto max-w-7xl px-5">
          <div className="grid items-start gap-12 lg:grid-cols-[1.1fr_1fr]">
            <Reveal>
              <Eyebrow>Victorian Energy Upgrades</Eyebrow>
              <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">
                Your air conditioning upgrade may qualify for <GradientText>VEU incentives.</GradientText>
              </h2>
              <p className="mt-5 text-base leading-relaxed text-mutedblue">
                Victorian households and businesses may be eligible for incentives when upgrading to
                approved high-efficiency reverse-cycle air conditioning systems.
              </p>
              <p className="mt-4 text-base leading-relaxed text-mutedblue">
                Airconditioning Victoria can assist customers with eligible installations and, where
                applicable, work with independent VEU Accredited Providers that process the
                applicable incentive and certificate requirements.
              </p>
              <div className="mt-8">
                <PrimaryButton to="/veu-rebates" testid="veu-section-cta">Check Rebate Eligibility</PrimaryButton>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="rounded-[18px] border-l-4 border-brand-cyan bg-white p-7 shadow-[0_8px_32px_rgba(5,59,117,0.05)]" data-testid="veu-info-box">
                <h3 className="font-display text-base font-bold text-navy">Important VEU Information</h3>
                <p className="mt-3 text-sm leading-relaxed text-mutedblue">
                  Eligible Victorian Energy Upgrades incentives are subject to current program
                  requirements, approved products, installation conditions and confirmation before
                  installation.
                </p>
                <p className="mt-3 text-sm leading-relaxed text-mutedblue">
                  Where applicable, rebate and certificate processing may be completed in
                  conjunction with a participating VEU Accredited Provider.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-white py-24 md:py-32" data-testid="process-section">
        <div className="mx-auto max-w-7xl px-5">
          <Reveal className="max-w-2xl">
            <Eyebrow>Simple Process</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">
              From quote to <GradientText>comfortable.</GradientText>
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s, i) => (
              <Reveal key={s.num} delay={i * 0.1}>
                <div
                  className="group relative h-full rounded-[20px] border border-cardline bg-white p-7 shadow-[0_8px_32px_rgba(5,59,117,0.04)] transition-[transform,border-color] duration-300 hover:-translate-y-1.5 hover:border-brand-cyan"
                  data-testid={`process-step-${s.num}`}
                >
                  <span className="font-display text-5xl font-extrabold text-mist transition-colors duration-300 group-hover:text-ice">
                    {s.num}
                  </span>
                  <span className="mt-4 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-ice">
                    <s.icon className="h-5 w-5 text-brand-blue" />
                  </span>
                  <h3 className="mt-4 font-display text-base font-bold text-navy">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-mutedblue">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-12">
            <PrimaryButton to="/contact" testid="process-cta">Get a Free Quote</PrimaryButton>
          </Reveal>
        </div>
      </section>

      {/* BRANDS */}
      <section className="bg-white py-24 md:py-28" data-testid="brands-section">
        <div className="mx-auto max-w-7xl px-5">
          <Reveal className="mx-auto max-w-2xl text-center">
            <Eyebrow className="justify-center">Leading Brands</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
              Quality systems from leading brands.
            </h2>
            <p className="mt-4 text-base leading-relaxed text-mutedblue">
              We can provide options across a range of leading air conditioning brands depending on
              system requirements, compatibility and availability.
            </p>
          </Reveal>
          <div className="mt-12 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {BRANDS.map((b, i) => (
              <Reveal key={b.name} delay={i * 0.05}>
                <div
                  className="flex h-24 items-center justify-center rounded-2xl border border-cardline bg-white px-6 transition-[border-color,transform,box-shadow] duration-300 hover:-translate-y-1 hover:border-brand-cyan hover:shadow-[0_14px_36px_rgba(5,59,117,0.08)]"
                  data-testid={`brand-${b.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                >
                  <img
                    src={b.logo}
                    alt={`${b.name} air conditioning`}
                    className="max-h-10 w-auto max-w-full object-contain md:max-h-11"
                    loading="lazy"
                  />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE AREAS */}
      <ServiceAreas />

      {/* QUICK QUOTE */}
      <QuickQuote />

      {/* HOME FAQ */}
      <section className="bg-white py-24 md:py-32" data-testid="home-faq-section">
        <div className="mx-auto max-w-4xl px-5">
          <Reveal className="text-center">
            <Eyebrow className="justify-center">Common Questions</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
              Frequently asked questions.
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="mt-12">
            <FAQ
              testidPrefix="home-faq"
              items={[
                { q: "Which type of air conditioning is right for my home?", a: "It depends on the size and layout of your home and the rooms you want to condition. A split system suits a single room or open-plan area, a multi-head system conditions several rooms from one outdoor unit, and ducted reverse-cycle is designed for whole-home comfort. We assess your property before recommending a system." },
                { q: "What is the difference between ducted and multi-head?", a: "Ducted systems deliver conditioned air through concealed ductwork and discreet ceiling outlets, while a multi-head system uses several indoor units connected to one shared outdoor condenser. Our Multi-Head Systems page includes a full side-by-side comparison." },
                { q: "Can reverse-cycle air conditioning provide heating?", a: "Yes. Reverse-cycle air conditioning provides both refrigerated cooling in summer and efficient electric heating in winter from the same system." },
                { q: "Are VEU incentives available for air conditioning upgrades?", a: "Selected approved high-efficiency reverse-cycle systems may be eligible for Victorian Energy Upgrades incentives, subject to current program requirements and confirmation before installation. See our VEU Rebates page for full details." },
                { q: "What information do you need to provide a quote?", a: "Your suburb, the type of property, the rooms or areas you want to heat and cool, and details of any existing heating or cooling equipment. Photos of the areas or your switchboard can also help." },
              ]}
            />
          </Reveal>
        </div>
      </section>

      <FinalCTA />
    </>
  );
}
